// Shared constants + helpers for the drag-and-drop label canvas designer.
// Positions are stored in mm; the backend converts at 8 dots/mm (203dpi).
export const PX_PER_MM = 6; // on-screen scale for the editor canvas

export const CANVAS_TYPES = [
  { v: "prep", label: "Kitchen Prep" },
  { v: "retail", label: "Retail" },
  { v: "docket", label: "Custom Docket" },
  { v: "equipment", label: "Equipment" },
];

// Font-size scale must mirror CANVAS_SIZE_MAP in backend/tspl_printer.py.
export const SIZE_OPTIONS = [
  { v: 1, label: "XS", dots: 12 },
  { v: 2, label: "S", dots: 16 },
  { v: 3, label: "M", dots: 24 },
  { v: 4, label: "L", dots: 48 },
  { v: 5, label: "XL", dots: 64 },
  { v: 6, label: "XXL", dots: 72 },
];
export const sizeDots = (s) => (SIZE_OPTIONS.find((o) => o.v === s) || SIZE_OPTIONS[2]).dots;
export const sizeToPx = (s) => (sizeDots(s) / 8) * PX_PER_MM;
export const sizeToMm = (s) => sizeDots(s) / 8;

// --- Font family + point size (mirror backend/tspl_printer._resolve_canvas_font) ---
// Each family maps to a TSPL font the TR-422B renders distinctly.
export const FONT_OPTIONS = [
  { v: "standard", label: "Standard", css: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif' },
  { v: "smooth", label: "Smooth (scalable)", css: '"Arial Narrow", "Roboto Condensed", "IBM Plex Sans", sans-serif', stretch: "condensed", tracking: "-0.02em" },
  { v: "ocr_b", label: "OCR-B (mono)", css: '"OCR B", "OCR-B", "Courier New", ui-monospace, monospace', tracking: "0.02em" },
];
export const PT_OPTIONS = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 56, 64, 72];

const _sizeToPt = (s) => (sizeDots(s) / 8) / 25.4 * 72;
// Effective point size of an element: explicit `pt`, else derived from legacy `size`.
export const elPt = (el) => {
  const pt = el?.pt;
  if (pt !== undefined && pt !== null && pt !== "" && Number(pt) > 0) return Number(pt);
  return Math.round(_sizeToPt(el?.size ?? 3));
};
export const elFont = (el) => (el?.font || "standard");
export const fontCss = (fam) => FONT_OPTIONS.find((f) => f.v === fam) || FONT_OPTIONS[0];
// Point size -> on-screen px height (203dpi-agnostic; matches sizeToPx scale).
export const ptToPx = (pt) => (Number(pt) / 72) * 25.4 * PX_PER_MM;

// Per-element layout extras (mirror backend tspl_printer canvas rendering).
export const ROTATIONS = [0, 90, 180, 270];
export const ALIGNMENTS = [
  { v: "left", label: "Left" },
  { v: "center", label: "Center" },
  { v: "right", label: "Right" },
];
export const elRot = (el) => {
  const r = Number(el?.rot || 0);
  return ROTATIONS.includes(r) ? r : 0;
};
export const elAlign = (el) => (["left", "center", "right"].includes(el?.align) ? el.align : "left");
export const elInvert = (el) => !!el?.invert;

// Shared on-screen text/field style so the designer canvas + read-only
// preview render identically (font, size, rotation, alignment, invert).
export const elTextStyle = (el, W) => {
  const fc = fontCss(elFont(el));
  const left = (el?.x || 0) * PX_PER_MM;
  const align = elAlign(el);
  const rot = elRot(el);
  const toEdge = Math.max(20, W - left - 2);
  const width = el?.w ? el.w * PX_PER_MM : (align !== "left" ? toEdge : undefined);
  const maxWidth = el?.w ? undefined : toEdge;
  return {
    fontSize: ptToPx(elPt(el)),
    fontWeight: el?.bold ? 800 : 400,
    fontFamily: fc.css,
    fontStretch: fc.stretch || "normal",
    letterSpacing: fc.tracking || "normal",
    textAlign: align,
    whiteSpace: "normal",
    wordBreak: "break-word",
    width,
    maxWidth,
    transform: rot ? `rotate(${rot}deg)` : undefined,
    transformOrigin: "top left",
    ...(elInvert(el) ? { background: "#111827", color: "#fff", padding: "1px 3px" } : null),
  };
};

// Available data fields per label type (key must match the backend
// render payload / _canvas_value resolver).
export const CANVAS_FIELDS = {
  prep: ["site_name", "product_name", "prep_date", "use_by_date", "storage", "staff_id", "allergens", "allergen_declaration"],
  retail: ["site_name", "product_name", "price", "best_before", "ingredients", "allergens", "allergen_declaration", "nutrition"],
  // Custom Docket is a free-form template — expose the full union of prep +
  // retail fields so any element can be placed on a custom label.
  docket: [
    "site_name", "product_name", "quantity", "price",
    "prep_date", "use_by_date", "best_before", "batch_number",
    "storage", "staff_id", "user_name", "ingredients",
    "allergens", "allergen_declaration", "nutrition", "notes",
  ],
  equipment: ["site_name", "equipment_name", "equipment_type", "equipment_location", "equipment_target"],
};

// Special (non-text) elements available per type.
export const CANVAS_SPECIALS = {
  prep: [],
  retail: [
    { type: "barcode", label: "Barcode" },
    { type: "qr", label: "Trust QR", source: "trust" },
  ],
  docket: [
    { type: "barcode", label: "Barcode" },
    { type: "qr", label: "QR code" },
  ],
  equipment: [{ type: "barcode", label: "Barcode" }],
};

export const FIELD_LABEL = {
  site_name: "Site name", product_name: "Product name", prep_date: "Prep date",
  use_by_date: "Use-by", storage: "Storage", staff_id: "Staff ID", user_name: "Staff",
  allergens: "Allergens", price: "Price", best_before: "Best before",
  allergen_declaration: "Allergen declaration",
  ingredients: "Ingredients", quantity: "Quantity", batch_number: "Batch",
  nutrition: "Nutrition panel",
  notes: "Notes", equipment_name: "Equipment name", equipment_type: "Type",
  equipment_location: "Location", equipment_target: "Target temp",
};

export const SAMPLE = {
  site_name: "HACCP AU", product_name: "Caesar Dressing", prep_date: "23/07/26 09:00",
  use_by_date: "26/07/26 09:00", storage: "REFRIGERATED", staff_id: "J. Smith",
  user_name: "J. Smith", allergens: "EGG, MILK, FISH", price: "$8.50",
  allergen_declaration: "Contains: Milk, Egg.\nMay be present: Peanut.",
  best_before: "20/06/2026", ingredients: "Beef, wheat flour, butter", quantity: "2L",
  batch_number: "BS-0900", notes: "Cool rapidly", equipment_name: "Walk-in Fridge 1",
  equipment_type: "fridge", equipment_location: "Kitchen", equipment_target: "0-5C",
  nutrition: "NUTRITION INFORMATION\nServings per pack: 8\nServing size: 60g\n              Per serve  Per 100g\nEnergy           891kJ    1485kJ\nProtein            3.6g       6g\nFat, total         7.4g    12.4g\n- saturated        3.7g     6.1g\nCarbohydrate      31.4g    52.3g\n- sugars          17.2g    28.7g\nSodium            186mg     310mg",
};

export function elementValue(el, data) {
  if (el.type === "text") return el.text || "";
  if (el.type === "barcode") return data && data.barcode ? `‖‖ ${data.barcode} ‖‖` : "‖‖ 9312345678907 ‖‖";
  if (el.type === "qr") return "QR";
  const src = data || SAMPLE;
  const v = src[el.key];
  if (v === undefined || v === null || v === "") return SAMPLE[el.key] ?? el.key;
  return v;
}

// A sensible starting layout: stack the type's fields + a special.
export function defaultCanvasLayout(type) {
  const fields = CANVAS_FIELDS[type] || [];
  let y = 3;
  const elements = fields.map((key, i) => {
    const size = key === "product_name" || key === "equipment_name" || key === "price" ? 5 : 3;
    const el = {
      id: `${key}-${i}`, type: "field", key, x: 3, y,
      size, bold: key === "product_name" || key === "allergens" || key === "allergen_declaration" || key === "equipment_name",
      visible: true,
    };
    y += sizeToMm(size) + 2;
    return el;
  });
  (CANVAS_SPECIALS[type] || []).forEach((sp, i) => {
    elements.push(
      sp.type === "qr"
        ? { id: `qr-${i}`, type: "qr", x: 40, y: 30, cell: 4, visible: true }
        : { id: `barcode-${i}`, type: "barcode", x: 3, y: 40, height: 8, visible: true },
    );
  });
  return { enabled: false, width_mm: 60, height_mm: 50, elements };
}
